import filter from "lodash/filter";
import { scopes } from "./enums";
import find from "lodash/find";
import tinycolor from "tinycolor2";
import axios from "axios";
import flatten from "lodash/flatten";
import { toast, Flip } from "react-toastify";

// map lookup source
export function mapLookup(i) {
    return {
        key: i.id,
        displayName: i.displayName,
    };
}

// map multiselect source
export function mapMultiselectLookup(i) {
    return {
        idx: i.id,
        displayName: i.displayName,
    };
}

// generic check handler
export function applyCheckedChange(idx, isChecked, ids, checkedList) {
    if (!ids) {
        return [];
    }

    if (idx === -1) {
        const diff = filter(checkedList, x => !ids.includes(x));

        return isChecked ? ids.concat(diff) : diff;
    }

    return isChecked ? checkedList.concat(idx) : filter(checkedList, i => i !== idx);
}

// default backend request
export function fetch({
    spinnerToggle, // spinner action creator
    request, // request function
    callback, // callback after successful execution
    errorCallback,
    validationCallback,
    errorOnWarning = false, // treats error as warning
}) {
    return () => {
        typeof spinnerToggle === "function" && spinnerToggle(true);

        return request()
            .then(r => {
                typeof spinnerToggle === "function" && spinnerToggle(false);

                // uses notifications from response
                parseNotifications({ warnings: r.data.warnings, errors: r.data.errors, infos: r.data.infos });

                // on error
                if (
                    r.data.errors &&
                    (r.data.errors.length > 0 || r.data.hasErrors || (r.data.hasWarnings && errorOnWarning))
                ) {
                    typeof errorCallback === "function" && errorCallback(r.data);
                    return;
                }

                typeof callback === "function" && callback(r.data);
            })
            .catch(reason => {
                typeof spinnerToggle === "function" && spinnerToggle(false);

                if (reason.response !== undefined) {
                    const { status, data } = reason.response;

                    if (status === 400 && data.errors?.length) {
                        typeof errorCallback === "function" && errorCallback(data.errors);
                    } else if (data.hasValidationErrors) {
                        typeof validationCallback === "function" && validationCallback(data.validationErrors);
                    } else if (data.hasErrors) {
                        parseNotifications({ errors: data.errors })
                    }

                    if (status === 403) {
                        window.location = "/forbidden";
                    }

                    // uses notifications from response
                    if (status >= 500) {
                        parseNotifications({ errors: ["Internal server error. Please, contact system administrator."] });
                    }
                }
                if (axios.isAxiosError(reason)) {
                    typeof errorCallback === "function" && errorCallback(reason.response?.data?.error ?? '', reason.response.data);
                } else {
                    typeof errorCallback === "function" && errorCallback(reason);
                }
            });
    };
}


// default backend request
export function fetchParallel({
    spinnerToggle, // spinner action creator
    requests, // request function
    callback, // callback after successful execution
}) {
    return () => {
        typeof spinnerToggle === "function" && spinnerToggle(true);

        return axios
            .all(requests.map(r => r()))
            .then(r => {
                typeof spinnerToggle === "function" && spinnerToggle(false);
                const warnings = flatten(r.map(d => d.data.warnings));
                const errors = flatten(r.map(d => d.data.errors));
                const infos = flatten(r.map(d => d.data.infos));

                // uses notifications from response
                parseNotifications({ warnings, errors, infos });

                // on error
                if (errors && errors.length > 0) {
                    return;
                }

                typeof callback === "function" && callback(r.map(m => m.data));
            })
            .catch(reason => {
                typeof spinnerToggle === "function" && spinnerToggle(false);
                parseNotifications({ errors: ["Internal server error. Please, contact system administrator."] });
                console.log("Reason: ", reason);
            });
    };
}

export function mapDashboardScenarioItem(i) {
    return {
        key: i.scenarioId,
        displayName: i.scenarioName,
        state: i.state,
        startDate: new Date(i.startDate),
        endDate: new Date(i.endDate),
    };
}
/* Cubes */
// gets default reports
export function getDefaultReports(libraries) {
    return libraries.map(l => ({
        idx: l.libraryId,
        libraryId: l.libraryId,
        libraryName: l.libraryName,
        files: l.libraryFiles.map(f => ({
            scopeId: scopes.notUsed,
            fileId: f.libraryFileId,
            fileName: f.displayName,
            hasAggregationScope: f.hasAggregationScope,
        })),
    }));
}

export function mapReports(libraries, reports) {
    return libraries.map(l => ({
        idx: l.libraryId,
        libraryId: l.libraryId,
        libraryName: l.libraryName,
        files: l.libraryFiles.map(f => ({
            scopeId: find(reports, r => r.fileId === f.libraryFileId).scopeId,
            fileId: f.libraryFileId,
            fileName: f.displayName,
            hasAggregationScope: f.hasAggregationScope,
        })),
    }));
}

export const getCubeLibraries = libraries => {
    return libraries.map(library => {
        return {
            ...library,
            libraryFiles: library.libraryFiles.filter(file => file.forCube),
        };
    });
};
/* end Cubes */

export const buildTree = function (items) {
    // Build adjacency list from [[key, {parent: key, displayName: 'text'}]]
    let tree = new Map();

    for (let { key, parent, displayName } of items) {
        tree.set(key, { parent, children: [], displayName });
    }
    for (let { key, parent } of items) {
        let parentNode = tree.get(parent);
        if (parentNode) parentNode.children.push(key);
    }
    return tree;
};

export const findRoots = tree => [...tree].filter(([_, value]) => ["", null].includes(value.parent));

export const downloadFile = function (content, fileName = "data.txt", type = "text/plain") {
    /*
    Create in-memory file with given content and download it.
     */
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([content], { type }));

    a.setAttribute("download", fileName);
    a.click();

    URL.revokeObjectURL(a.href);
};

export const colorGradient = function (fade, startRgb, endRgb, fadeAlpha = false, defaultAlpha = 0.8) {
    const alpha = fadeAlpha ? startRgb.a + (endRgb.a - startRgb.a) * fade : defaultAlpha;

    return {
        r: parseInt(startRgb.r + (endRgb.r - startRgb.r) * fade),
        g: parseInt(startRgb.g + (endRgb.g - startRgb.g) * fade),
        b: parseInt(startRgb.b + (endRgb.b - startRgb.b) * fade),
        a: alpha,
    };
};

export const color2Style = rgb => ({ background: tinycolor(rgb).toRgbString() });

export const parseNotifications = ({ infos, warnings, errors }) => {
    if (infos) {
        infos.map(message => {
            toast.success(message, { containerId: "A", transition: Flip });
        });
    }

    if (warnings) {
        warnings.map(message => {
            toast.warn(message, { containerId: "A", transition: Flip });
        });
    }

    if (errors) {
        errors.map(message => {
            toast.error(message, { containerId: "A", transition: Flip });
        });
    }
};

export const displayErrors = function (errors) {
    if (errors) {
        parseNotifications({ errors: [].concat(errors) });
    }
};

export const displayValidationErrors = function (validationErrors) {
    if (validationErrors) {
        parseNotifications({ warnings: [].concat(validationErrors) });
    }
};

export function formatTimeSpan(period) {
    //period can be negative. For example -2h

    if (!period || period.startsWith('-')) {
        return '(deleting)';
    }

    let days = 0;
    let timeParts = period.split('.');
    let time;

    if (timeParts.length === 3) {
        // Format with days: '3.20:58:17.0400000'
        days = parseInt(timeParts[0], 10);
        time = timeParts[1];
    } else {
        // Format without days: '20:58:17.0400000'
        time = timeParts[0];
    }

    let [hours, minutes] = time.split(':');
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);

    if (days > 36500) {
        return '';
    } else if (days > 0 && days < 36500) {
        return `(${days}d)`;
    } else if (days <= 0 && hours > 0) {
        return `(${hours}h)`;
    } else if (days <= 0 && hours <= 0 && minutes > 0) {
        return `(${minutes}m)`;
    } else {
        return '(deleting)';
    }
}

